import { createContext, useReducer, useRef } from 'react';
import { AppState } from '../state/app.state';
import { AppAction } from '../action/app.action';
import { AppReducer } from '../reducer/app.reducer';
import { TestInitData } from '../model/test-init.model';
import {
  Question,
  Section,
  StartTestRes,
  TestRes,
} from '../response/test.response';
import { QuestionType } from '../enum/question-type.enum';
import { useTimer } from '../hook/timer.hook';
import { useSaveResponse } from '../page/shared/hook/save-response';
import { CandidateResRes } from '../response/candidate-response.response';
import { useI18n } from '../i18n/i18n.hook';
import { WhiteLabel } from '../response/white-label.response';

const initialState = {
  loading: false,
  testInitData: {} as TestInitData,
  candidateResponse: {} as CandidateResRes,
  inProgress: false,
  lastSaveTime: new Date().getTime(),
  noInternet: false,
} as AppState;

export const AppContext = createContext<any>(initialState);

export const AppStateProvider = ({ children }: any) => {
  const [state, dispatch] = useReducer(AppReducer, initialState);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { reSetTimer } = useTimer();
  const { setLanguage } = useI18n();

  const { saveTest } = useSaveResponse();

  const initTest = (testId: string) => {
    dispatch({
      type: AppAction.INIT_TEST,
      payload: {
        testInitData: {
          id: testId,
        },
      },
    });
  };

  const updateLoading = (loading: boolean) => {
    dispatch({
      type: AppAction.UPDATE_LOADING,
      payload: { loading },
    });
  };

  const addTestData = (startTestRes: StartTestRes, isMobile: boolean) => {
    dispatch({
      type: AppAction.ADD_TEST_DATA,
      payload: {
        testRes: {
          ...startTestRes.test,
          sectionList: startTestRes.test.sectionList
            .sort((a: Section, b: Section) => {
              // Sort sections by their level or index if available
              return (a.order || 0) - (b.order || 0);
            })
            .map((section: Section) => ({
              ...section,
              questionList: section.questionList.sort(
                (a: Question, b: Question) => {
                  // Check if questions have paragraph
                  const aHasParagraph = a.paragraph?.isParagraph || false;
                  const bHasParagraph = b.paragraph?.isParagraph || false;

                  // If one has paragraph and other doesn't, prioritize paragraph questions at position 3
                  if (aHasParagraph && !bHasParagraph) {
                    return 1; // a goes after b (paragraph questions go later)
                  }
                  if (!aHasParagraph && bHasParagraph) {
                    return -1; // a goes before b (non-paragraph questions go first)
                  }

                  // If both have paragraph or both don't have paragraph, sort by type
                  const typeOrder = {
                    [QuestionType.SINGLE_CHOICE]: 1,
                    [QuestionType.MULTIPLE_CHOICE]: 2,
                    [QuestionType.INT_ANS]: 3,
                    [QuestionType.RANGE_ANS]: 4,
                    [QuestionType.MATRIX_MATCH]: 5,
                    [QuestionType.LONG_ANSWER]: 6,
                  };

                  const aTypeOrder = typeOrder[a.type] || 999;
                  const bTypeOrder = typeOrder[b.type] || 999;

                  // Sort by question type order
                  if (aTypeOrder !== bTypeOrder) {
                    return aTypeOrder - bTypeOrder;
                  }

                  // If same type, sort by original order
                  if (section.shuffling) {
                    return 0;
                  } else {
                    return a.order - b.order;
                  }
                },
              ),
            })),
        },
        candidateResponse: startTestRes.candidateResponse,
        inProgress: startTestRes.inProgress,
        isMobile: isMobile,
      },
    });
  };

  const updateActiveSection = (sectionId: string, questionId?: string) => {
    const timeTaken = reSetTimer(
      state.testRes?.duration,
      state.testRes?.timeTaken,
    );
    dispatch({
      type: AppAction.UPDATE_ACTIVE_SECTION,
      payload: { sectionId, questionId, timeTaken },
    });
  };

  const updateActiveSectionWithQuestionType = (
    sectionId: string,
    questionType: string,
  ) => {
    const timeTaken = reSetTimer(
      state.testRes?.duration,
      state.testRes?.timeTaken,
    );
    dispatch({
      type: AppAction.UPDATE_ACTIVE_SECTION_WITH_QUESTION_TYPE,
      payload: { sectionId, questionType, timeTaken },
    });
  };

  const updateActiveQuestion = (questionId: string) => {
    const timeTaken = reSetTimer(
      state.testRes?.duration,
      state.testRes?.timeTaken,
    );
    dispatch({
      type: AppAction.UPDATE_ACTIVE_QUESTION,
      payload: { questionId, timeTaken },
    });
  };

  const markQuestionForRevisit = (questionId: string) => {
    dispatch({
      type: AppAction.MARK_QUESTION_REVISIT,
      payload: { questionId },
    });
  };

  const markQuestionAsSaved = (questionId: string) => {
    dispatch({
      type: AppAction.MARK_QUESTION_SAVED,
      payload: { questionId },
    });
  };

  const saveQuestionResponse = (
    questionId: string,
    candidateAnswer?: string,
    optionIdList?: any[],
  ) => {
    dispatch({
      type: AppAction.SAVE_QUESTION_RES,
      payload: { questionId, candidateAnswer, optionIdList },
    });
  };

  const clearQuestionResponse = (questionId: string) => {
    dispatch({
      type: AppAction.CLEAR_QUESTION_RES,
      payload: { questionId },
    });
  };

  const moveNext = () => {
    let activeSectionIndex = -1;
    let nextSectionId = '';

    let activeQuestionIndex = -1;
    let nextQuestionId = '';

    const activeSection = state.testRes.sectionList.filter(
      (section: Section, sIndex: number) => {
        if (nextSectionId === '' && activeSectionIndex !== -1) {
          nextSectionId = section.id;
        }
        if (section.active) {
          activeSectionIndex = sIndex;
        }
        return section.active;
      },
    )[0];
    activeSection.questionList.map((question: Question, index: number) => {
      if (nextQuestionId === '' && activeQuestionIndex !== -1) {
        nextQuestionId = question.id;
      }
      if (question.active) {
        activeQuestionIndex = index;
      }
    });

    if (nextQuestionId.length > 0) {
      updateActiveQuestion(nextQuestionId);
    } else if (nextSectionId.length > 0) {
      updateActiveSection(nextSectionId);
    }
  };

  const movePrevious = () => {
    let activeSectionIndex = -1;
    let previousSectionId = '';

    const activeSection = state.testRes.sectionList.filter(
      (section: Section, sIndex: number) => {
        if (section.active) {
          activeSectionIndex = sIndex;
        }
        if (activeSectionIndex === -1) {
          previousSectionId = section.id;
        }
        return section.active;
      },
    )[0];

    let activeQuestionIndex = -1;
    let previousQuestionId = '';

    activeSection.questionList.map((question: Question, index: number) => {
      if (question.active) {
        activeQuestionIndex = index;
      }
      if (activeQuestionIndex === -1) {
        previousQuestionId = question.id;
      }
    });

    if (previousQuestionId.length > 0) {
      updateActiveQuestion(previousQuestionId);
    } else if (previousSectionId.length > 0) {
      updateActiveSection(previousSectionId);
    }
  };

  const moveNextQuestion = () => {
    let activeQuestionIndex = -1;
    let nextQuestionId = '';

    const activeSection = state.testRes.sectionList.filter(
      (section: Section, sIndex: number) => {
        return section.active;
      },
    )[0];
    activeSection.questionList.map((question: Question, index: number) => {
      if (nextQuestionId === '' && activeQuestionIndex !== -1) {
        nextQuestionId = question.id;
      }
      if (question.active) {
        activeQuestionIndex = index;
      }
    });

    if (nextQuestionId.length > 0) {
      updateActiveQuestion(nextQuestionId);
    }
  };

  const movePreviousQuestion = () => {
    const activeSection = state.testRes.sectionList.filter(
      (section: Section, sIndex: number) => {
        return section.active;
      },
    )[0];

    let activeQuestionIndex = -1;
    let previousQuestionId = '';

    activeSection.questionList.map((question: Question, index: number) => {
      if (question.active) {
        activeQuestionIndex = index;
      }
      if (activeQuestionIndex === -1) {
        previousQuestionId = question.id;
      }
    });

    if (previousQuestionId.length > 0) {
      updateActiveQuestion(previousQuestionId);
    }
  };

  const updateLastSaveTime = (lastSaveTime: number) => {
    dispatch({
      type: AppAction.UPDATE_LAST_SAVE_TIME,
      payload: { lastSaveTime },
    });
  };

  const updateCandidateData = (candidateResponse: CandidateResRes) => {
    dispatch({
      type: AppAction.UPDATE_CANDIDATE_DATA,
      payload: { candidateResponse },
    });
  };

  const saveResponse = async (
    testRes: TestRes,
    submitted: boolean,
    handleClose: () => void,
  ) => {
    if (retryTimeoutRef.current && submitted) {
      clearTimeout(retryTimeoutRef.current);
    }
    if (submitted) {
      updateLoading(true);
    }
    const [data, err] = await saveTest(testRes, submitted);

    if (!err) {
      if (data.submitted || submitted) {
        handleClose();
      }
      updateCandidateData(data.candidateResponse);
      updateLastSaveTime(new Date().getTime());
      dispatch({
        type: AppAction.UPDATE_NO_INTERNET,
        payload: { noInternet: false },
      });
    } else {
      dispatch({
        type: AppAction.UPDATE_NO_INTERNET,
        payload: { noInternet: true },
      });
      // Clear any existing retry timeout before setting a new one
      if (submitted) {
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          saveResponse(testRes, submitted, handleClose);
        }, 10000);
      }
    }
  };

  const updateLanguage = (language: string) => {
    dispatch({
      type: AppAction.UPDATE_LANGUAGE,
      payload: { language },
    });
    setLanguage(language);
  };

  const updateWhiteLabel = (whiteLabel: WhiteLabel) => {
    dispatch({
      type: AppAction.UPDATE_WHITE_LABEL,
      payload: {
        whiteLabel: whiteLabel,
      },
    });
  };

  const value = {
    loading: state.loading,
    testInitData: state.testInitData,
    testRes: state.testRes,
    lastSaveTime: state.lastSaveTime,
    inProgress: state.inProgress,
    candidateResponse: state.candidateResponse,
    language: state.language,
    whiteLabel: state.whiteLabel,
    noInternet: state.noInternet,
    initTest,
    addTestData,
    updateLoading,
    updateActiveQuestion,
    updateActiveSection,
    updateActiveSectionWithQuestionType,
    markQuestionForRevisit,
    markQuestionAsSaved,
    saveQuestionResponse,
    clearQuestionResponse,
    moveNext,
    movePrevious,
    moveNextQuestion,
    movePreviousQuestion,
    saveResponse,
    updateLanguage,
    updateWhiteLabel,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
